import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip, message } from 'antd'
import { useMutation } from '@tanstack/react-query'

import useClient from 'hooks/useClient'
import PageTable from 'components/PageTable'
import Button from 'components/Button'
import Icon from 'components/Icon'

import { ICONS, PERMISSIONS } from 'utils/constants'
import {
  getAdvertisements,
  createAdvertisement,
  updateAdvertisement,
  deleteAdvertisement,
} from 'redux/actions/advertisements'
import AdvertisementFormModal from 'components/Modal/modals/AdvertisementFormModal/AdvertisementFormModal'
import { uploadBulkFiles } from 'api/files'

const Advertisements = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isClientCan } = useClient()

  const canRead = isClientCan(PERMISSIONS.READ_ADVERTISEMENTS)
  const canCreate = isClientCan(PERMISSIONS.CREATE_ADVERTISEMENTS)
  const canUpdate = isClientCan(PERMISSIONS.UPDATE_ADVERTISEMENTS)
  const canDelete = isClientCan(PERMISSIONS.DELETE_ADVERTISEMENTS)

  const { items, isGetLoading, isChangeLoading } = useSelector((state) => state.advertisements)

  const [activeAdvertisement, setActiveAdvertisement] = useState(null)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const uploadBulkFilesMutation = useMutation(
    ({ files, onProgress }) => uploadBulkFiles(files, { onProgress }),
    {
    onError: () => {
      message.error('Failed to upload image')
    },
  })

  useEffect(() => {
    if (canRead) {
      dispatch(getAdvertisements())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead])

  const openCreateModal = () => {
    setActiveAdvertisement(null)
    setIsModalVisible(true)
  }

  const openEditModal = (advertisement) => {
    setActiveAdvertisement(advertisement)
    setIsModalVisible(true)
  }

  const closeModal = useCallback(() => {
    setIsModalVisible(false)
    setActiveAdvertisement(null)
  }, [])

  const handleSubmit = useCallback(
    async (data) => {
      const { imageFile, bannerImageFile, imageArFile, bannerImageArFile, ...rest } = data

      let imageId = activeAdvertisement?.image?.id || null
      let bannerImageId = activeAdvertisement?.bannerImage?.id || null
      let imageArId = activeAdvertisement?.imageAr?.id || null
      let bannerImageArId = activeAdvertisement?.bannerImageAr?.id || null

      if (imageFile) {
        try {
          const progressKey = 'advertisement-image-upload-progress'
          const entities = await uploadBulkFilesMutation.mutateAsync({
            files: [imageFile],
            onProgress: (percent) => {
              message.open({
                key: progressKey,
                type: 'loading',
                duration: 0,
                content: `Uploading image... ${percent}%`,
              })
            },
          })
          message.destroy(progressKey)
          if (entities && entities[0]?.id) {
            imageId = entities[0].id
          }
        } catch (error) {
          message.destroy('advertisement-image-upload-progress')
          return
        }
      }

      if (bannerImageFile) {
        try {
          const progressKey = 'advertisement-dark-image-upload-progress'
          const entities = await uploadBulkFilesMutation.mutateAsync({
            files: [bannerImageFile],
            onProgress: (percent) => {
              message.open({
                key: progressKey,
                type: 'loading',
                duration: 0,
                content: `Uploading dark image... ${percent}%`,
              })
            },
          })
          message.destroy(progressKey)
          if (entities && entities[0]?.id) {
            bannerImageId = entities[0].id
          }
        } catch (error) {
          message.destroy('advertisement-dark-image-upload-progress')
          return
        }
      }

      if (imageArFile) {
        try {
          const progressKey = 'advertisement-image-ar-upload-progress'
          const entities = await uploadBulkFilesMutation.mutateAsync({
            files: [imageArFile],
            onProgress: (percent) => {
              message.open({
                key: progressKey,
                type: 'loading',
                duration: 0,
                content: `Uploading Arabic banner... ${percent}%`,
              })
            },
          })
          message.destroy(progressKey)
          if (entities && entities[0]?.id) {
            imageArId = entities[0].id
          }
        } catch (error) {
          message.destroy('advertisement-image-ar-upload-progress')
          return
        }
      }

      if (bannerImageArFile) {
        try {
          const progressKey = 'advertisement-dark-image-ar-upload-progress'
          const entities = await uploadBulkFilesMutation.mutateAsync({
            files: [bannerImageArFile],
            onProgress: (percent) => {
              message.open({
                key: progressKey,
                type: 'loading',
                duration: 0,
                content: `Uploading Arabic dark banner... ${percent}%`,
              })
            },
          })
          message.destroy(progressKey)
          if (entities && entities[0]?.id) {
            bannerImageArId = entities[0].id
          }
        } catch (error) {
          message.destroy('advertisement-dark-image-ar-upload-progress')
          return
        }
      }

      const payload = { ...rest, imageId, bannerImageId, imageArId, bannerImageArId }

      if (activeAdvertisement) {
        dispatch(
          updateAdvertisement({
            id: activeAdvertisement.id,
            data: payload,
            onSuccess: () => {
              closeModal()
              dispatch(getAdvertisements())
            },
          }),
        )
      } else {
        dispatch(
          createAdvertisement({
            data: payload,
            onSuccess: () => {
              closeModal()
              dispatch(getAdvertisements())
            },
          }),
        )
      }
    },
    [
      activeAdvertisement,
      dispatch,
      uploadBulkFilesMutation,
      closeModal,
    ],
  )

  const columns = useMemo(() => {
    const cols = []

    cols.push({
      title: '№',
      dataIndex: 'id',
      render: (text, record, index) => index + 1,
      key: 'order',
      width: 60,
      align: 'center',
    })

    cols.push({
      title: 'Placement',
      dataIndex: 'advertisementType',
      key: 'advertisementType',
      width: 110,
      render: (v) => v || '-',
    })

    cols.push({
      title: 'Link Target',
      dataIndex: 'linkType',
      key: 'linkType',
      width: 110,
      render: (v) => v || '-',
    })

    cols.push({
      title: 'Target',
      key: 'target',
      width: 240,
      render: (_, record) => {
        if (record?.linkType === 'CATEGORY') {
          const sizes = Array.isArray(record?.productSizes) && record.productSizes.length
            ? ` (${record.productSizes.join(', ')})`
            : ''
          return `${record?.category?.name || '-'}${sizes}`
        }
        if (record?.linkType === 'PRODUCT') {
          return record?.product?.title || '-'
        }
        return '-'
      },
    })

    if (canUpdate) {
      cols.push({
        title: t('fields.visibility'),
        align: 'center',
        width: 120,
        key: 'visibility',
        render: (text, { id, isVisible }) => (
          <div className="table-actions">
            <Tooltip placement="topRight" title={isVisible ? t('app.hide') : t('app.show')}>
              <button
                className="action-btn action-icon--visibility"
                onClick={(event) => {
                  event.stopPropagation()
                  dispatch(
                    updateAdvertisement({
                      id,
                      data: { isVisible: !isVisible },
                      onSuccess: () => dispatch(getAdvertisements()),
                    }),
                  )
                }}
              >
                <Icon
                  className="action-icon"
                  icon={isVisible ? ICONS.VISIBLE : ICONS.INVISIBLE}
                  size={20}
                />
              </button>
            </Tooltip>
          </div>
        ),
      })
    }

    if (canUpdate || canDelete) {
      cols.push({
        title: t('fields.actions', 'Actions'),
        key: 'actions',
        align: 'center',
        width: 140,
        render: (text, record) => (
          <div className="table-actions">
            {canUpdate && (
              <Tooltip placement="topRight" title={t('app.edit')}>
                <button
                  className="action-btn action-icon--edit"
                  onClick={(event) => {
                    event.stopPropagation()
                    openEditModal(record)
                  }}
                >
                  <Icon className="action-icon" icon={ICONS.EDIT} size={20} />
                </button>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip placement="topRight" title={t('app.delete')}>
                <button
                  className="action-btn action-icon--delete"
                  onClick={(event) => {
                    event.stopPropagation()
                    dispatch(
                      deleteAdvertisement({
                        id: record.id,
                        onSuccess: () => dispatch(getAdvertisements()),
                      }),
                    )
                  }}
                >
                  <Icon className="action-icon" icon={ICONS.TRASH} size={20} />
                </button>
              </Tooltip>
            )}
          </div>
        ),
      })
    }

    return cols
  }, [t, canUpdate, canDelete, dispatch])

  return (
    <>
      <PageTable
        pageTitle={t('advertisements.title', 'Banners')}
        action={
          canCreate ? (
            <Button type="primary" onClick={openCreateModal}>
              <Icon icon={ICONS.PLUS} className="btn-icon-suffix" />
              <span className="btn-text">{t('advertisements.add button', 'Add banner')}</span>
            </Button>
          ) : null
        }
        baseData={items}
        columns={columns}
        loading={isGetLoading}
        pagination={false}
        onRow={
          canUpdate
            ? (record) => ({
                onClick: () => openEditModal(record),
              })
            : null
        }
      />
      <AdvertisementFormModal
        advertisement={activeAdvertisement}
        isVisible={isModalVisible}
        onCancel={closeModal}
        submitCallback={handleSubmit}
        loading={isChangeLoading || uploadBulkFilesMutation.isLoading}
      />
    </>
  )
}

export default Advertisements
